import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  FaEye,
  FaPrint,
  FaFileExcel,
  FaFilePdf,
  FaTimes,
  FaChartBar,
  FaFileInvoiceDollar,
  FaRegMoneyBillAlt,
  FaCalculator,
} from "react-icons/fa";
import {
  getProjectSummary,
  clearProjectSummary,
  clearError,
  clearSuccess,
  getProjectsForReport,
} from "../../../../redux/Slices/ProjectReportsSlice";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import i18n from "../../../../i18n/i18n";
import {
  BASE_URL,
  getToken,
  convertArabicNumbersToEnglish,
} from "../../../../shared/utils.js/utils";
import {
  saveDropdownCache,
  getDropdownCache,
  generateDropdownCacheKey,
  isOnline,
  getAllDropdownCacheForQuery,
} from "../../../../shared/utils.js/offlineQueue";
import axios from "axios";

const ProjectSummary = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const dir = isRTL ? "rtl" : "ltr";
  const headerAlign = isRTL ? "right" : "left";
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  const { projectSummary, projects, isLoading, error, success } = useSelector(
    state => state.projectReports,
  );

  const [projectId, setProjectId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const projectSearchInputRef = useRef(null);
  const [pageMinHeight, setPageMinHeight] = useState("auto");

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [projectsPagination, setProjectsPagination] = useState(null);

  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const lastProjectSearchRef = useRef("");
  const lastProjectPageRef = useRef(1);

  // ----------------------------------------------------
  // المحرك المحاسبي والتحليلي المتطور (Accounting & Variation Engine)
  // ----------------------------------------------------
  const rawSummaryData = projectSummary?.data || projectSummary || {};
  const summaryData = {
    ...rawSummaryData,
    project: rawSummaryData.project || rawSummaryData.project_details,
    contract: rawSummaryData.contract || rawSummaryData.contracts?.items?.[0],
    quotation:
      rawSummaryData.quotation || rawSummaryData.quotations?.items?.[0],
  };

  // استخراج النسب المئوية للعقد كحالة قابلة للتعديل من المستخدم لو الباك إند مش باعتها
  const defaultAdvPct =
    summaryData.project?.contract?.advance_payment_percent ||
    summaryData.contract?.advance_payment_percent ||
    summaryData.project?.advance_payment_percent ||
    0;
  const defaultRetPct =
    summaryData.project?.contract?.retention_percent ||
    summaryData.contract?.retention_percent ||
    summaryData.project?.retention_percent ||
    0;
  const defaultDedPct =
    summaryData.project?.contract?.quotation?.discount_percent ||
    summaryData.contract?.quotation?.discount_percent ||
    summaryData.quotation?.discount_percent ||
    0;

  const [manualAdvPct, setManualAdvPct] = useState(parseFloat(defaultAdvPct));
  const [manualRetPct, setManualRetPct] = useState(parseFloat(defaultRetPct));
  const [manualDedPct, setManualDedPct] = useState(parseFloat(defaultDedPct));

  // تحديث الحالات لو الداتا اتغيرت
  useEffect(() => {
    setManualAdvPct(parseFloat(defaultAdvPct));
    setManualRetPct(parseFloat(defaultRetPct));
    setManualDedPct(parseFloat(defaultDedPct));
  }, [defaultAdvPct, defaultRetPct, defaultDedPct]);

  const ledgerData = useMemo(() => {
    // 2. تجميع المستخلصات والتحصيلات
    const bills =
      summaryData.progress_bills?.items || summaryData.progress_bills || [];
    const payments =
      summaryData.payments?.items ||
      summaryData.payments ||
      summaryData.received_payments ||
      summaryData.receipt_vouchers ||
      summaryData.receipts ||
      summaryData.vouchers ||
      [];

    // 1. التقاط القيمة التعاقدية الصحيحة من أي مكان متوفر سواء project أو أول مستخلص
    const firstBill = bills.length > 0 ? bills[0] : {};
    let contractValue = parseFloat(
      summaryData.project?.contract_value ||
        firstBill.project?.contract_value ||
        summaryData.financial?.total_contracts ||
        0,
    );
    if (contractValue === 0 && summaryData) {
      const strData = JSON.stringify(summaryData);
      const matchVal = strData.match(
        /"contract_value"\s*:\s*"?(\d+(\.\d+)?)"?/,
      );
      if (matchVal) contractValue = parseFloat(matchVal[1]);
    }

    // ترتيب المستخلصات زمنياً لمعالجة القيم التراكمية
    const sortedBillsOriginal = [...bills].sort(
      (a, b) =>
        new Date(a.date || a.created_at) - new Date(b.date || b.created_at),
    );

    const getInvoiceValue = (bill, key) => {
      if (bill && bill.invoice_summary && Array.isArray(bill.invoice_summary)) {
        const item = bill.invoice_summary.find(s => s.key === key);
        if (item) return parseFloat(item.value || 0);
      }
      return null;
    };

    let previousGross = 0;
    const normalizedBills = sortedBillsOriginal
      .filter(b => b != null)
      .map(b => {
        const workDone =
          getInvoiceValue(b, "total_amount") || parseFloat(b.total_amount || 0);
        const taxAmount =
          getInvoiceValue(b, "tax_amount") || parseFloat(b.tax_amount || 0);

        // استخراج ضريبة الخصم بطريقة ذكية لو مش موجودة في الـ invoice_summary
        let dedTax = getInvoiceValue(b, "deduction_tax_amount");
        if (dedTax === null) {
          // استنتاج ضريبة الخصم لو كانت محسوبة في الـ net_amount
          const netAmt = parseFloat(b.net_amount || 0);
          if (netAmt > 0 && b.total_amount && b.tax_amount) {
            dedTax = workDone + taxAmount - netAmt;
          }
        }
        if (dedTax === null || isNaN(dedTax) || dedTax === 0) {
          const dedPct = parseFloat(
            b.contract?.quotation?.discount_percent || manualDedPct || 0,
          );
          dedTax = (workDone * dedPct) / 100;
        }

        // استخراج الدفعة المقدمة كما جاءت من الباك إند بدقة
        let advanceDed = getInvoiceValue(b, "advance_payment_deduction");
        if (advanceDed === null)
          advanceDed = parseFloat(b.advance_payment_deduction || 0);

        // استخراج الخصم كما جاء من الباك إند بدقة
        let retDed = getInvoiceValue(b, "retention_amount");
        if (retDed === null) retDed = parseFloat(b.retention_amount || 0);

        // تصحيح إجمالي المستخلص التراكمي: الأولوية دايماً للقيمة الجاية من الباك إند (إجمالي قيمة المستخلص)
        // عشان كشف الحساب التحليلي يوزن بالمللي مع السداد الفعلي اللي حصل (زي ما هو في الداتا بيز)
        let backendGross = getInvoiceValue(b, "gross_amount");

        // لو مش موجود، نحسبه مانيوال بس من غير ما نخصم الدفعة المقدمة لو هي فعلياً صفر في الداتا بيز
        const currentGross =
          backendGross !== null
            ? backendGross
            : workDone +
              taxAmount -
              dedTax -
              parseFloat(b.advance_payment_deduction || 0) -
              retDed;

        // استنتاج قيمة المستخلص الحالي الصافية بطرح القيمة التراكمية السابقة
        const currentNet = currentGross - previousGross;

        previousGross = currentGross;

        // حفظ القيم المصححة للاستخدام في المجاميع النهائية يتم في الكائن الجديد لتجنب خطأ object is not extensible
        return {
          id: b.id,
          date: b.date || b.created_at,
          code: b.bill_no || b.code || b.bill_number || "مستخلص جاري",
          type: "bill",
          description: isRTL
            ? `مستخلص أعمال معتمد رقم (${b.bill_no || b.code || b.id})`
            : `Approved Progress Bill No. (${b.bill_no || b.code || b.id})`,
          notes: b.notes || "",
          debit: currentNet, // القيمة المصححة للمطالبة
          credit: 0,
          // قيم محسوبة لتسوية المشروع النهائي
          _calculated_advance: advanceDed,
          _calculated_retention: retDed,
          _calculated_gross: currentGross,
          _calculated_dedTax: dedTax,
          _calculated_workDone: workDone,
          _calculated_tax: taxAmount,
        };
      });

    const normalizedPayments = payments
      .filter(p => p != null)
      .map(p => ({
        id: p.id,
        date: p.voucher_date || p.date || p.payment_date || p.created_at,
        code:
          p.reference ||
          p.code ||
          p.reference_number ||
          p.receipt_number ||
          p.voucher_number ||
          "إيصال استلام",
        type: "payment",
        description: isRTL
          ? `إيصال استلام نقدية - سند قبض رقم (${p.reference || p.code || p.id})`
          : `Received Payment - Voucher No. (${p.reference || p.code || p.id})`,
        notes: p.notes || "",
        debit: 0,
        credit: parseFloat(
          p.total_amount || p.amount || p.collected_amount || p.value || 0,
        ),
      }));

    const normalizedTransactions = [
      ...normalizedBills,
      ...normalizedPayments,
    ].sort((a, b) => new Date(a.date) - new Date(b.date));

    // 3. حساب إجمالي المستخلصات والتحصيلات بشكل ديناميكي ودقيق من العمليات الفعلية
    const totalBilled = normalizedTransactions.reduce(
      (sum, tx) => sum + tx.debit,
      0,
    );
    const totalCollected = normalizedTransactions.reduce(
      (sum, tx) => sum + tx.credit,
      0,
    );

    // حساب التفاصيل الإضافية للتسوية المحاسبية للمشروع (بأخذ أقصى قيمة تراكمية لتفادي تصفير القيم بسبب المستخلصات الفارغة/المسودة)
    // حساب قيمة الدفعة المقدمة وقيمة التأمين بناءً على القيمة التعاقدية ونسب العقد (بناءً على طلب العميل)
    const totalRetention = (contractValue * manualRetPct) / 100;
    const totalAdvanceDeduction = (contractValue * manualAdvPct) / 100;

    const totalTax =
      normalizedBills.length > 0
        ? Math.max(...normalizedBills.map(b => b._calculated_tax || 0))
        : 0;
    const totalDeductionTax =
      normalizedBills.length > 0
        ? Math.max(...normalizedBills.map(b => b._calculated_dedTax || 0))
        : 0;
    const totalGrossBilled =
      normalizedBills.length > 0
        ? Math.max(...normalizedBills.map(b => b._calculated_gross || 0))
        : 0;
    const totalWorkDone =
      normalizedBills.length > 0
        ? Math.max(...normalizedBills.map(b => b._calculated_workDone || 0))
        : 0;

    // 4. حساب المتبقي من العقد أو الزيادة في قيمة المستخلصات بشكل ذكي واحترافي
    const isOverContract = totalWorkDone > contractValue;
    const remainingContractOrOverage = isOverContract
      ? totalWorkDone - contractValue
      : contractValue - totalWorkDone;

    // 5. بناء الرصيد التراكمي المتحرك (Running Balance) خطوة بخطوة
    let currentBalance = 0;
    const StatementOfAccount = normalizedTransactions.map(tx => {
      currentBalance += tx.debit - tx.credit;
      return {
        ...tx,
        runningBalance: currentBalance,
      };
    });

    // 6. مؤشرات الأداء والتحليل المتقدم (Financial KPIs)
    const financialProgressPct =
      contractValue > 0 ? (totalWorkDone / contractValue) * 100 : 0;
    const collectionPct =
      totalBilled > 0 ? (totalCollected / totalBilled) * 100 : 0;
    const netOutstanding = totalBilled - totalCollected;

    return {
      contractValue,
      totalBilled,
      totalCollected,
      netOutstanding,
      financialProgressPct,
      collectionPct,
      StatementOfAccount,
      isOverContract,
      remainingContractOrOverage,
      totalRetention,
      totalAdvanceDeduction,
      totalTax,
      totalDeductionTax,
      totalGrossBilled,
      totalWorkDone,
    };
  }, [projectSummary, isRTL, manualAdvPct, manualRetPct, manualDedPct]);

  // ----------------------------------------------------
  // دوال جلب البيانات والتهيئة والتنقل الكلاسيكية
  // ----------------------------------------------------
  const getProjectName = project => {
    if (!project) return "";
    return isRTL
      ? project.name_ar || project.name_en || ""
      : project.name_en || project.name_ar || "";
  };

  const getCurrentProjectName = () => {
    const project = summaryData.project || selectedProject;
    if (!project) return "—";
    if (project.name_ar || project.name_en || project.name) {
      return isRTL
        ? project.name_ar || project.name_en || project.name || "—"
        : project.name_en || project.name || project.name_ar || "—";
    }
    return "—";
  };

  const getCustomerName = () => {
    const project = summaryData.project || selectedProject;
    if (!project) return "—";
    if (project.customer_name) return project.customer_name;
    const customer = project.customer;
    if (!customer) return "—";
    return isRTL
      ? customer.name_ar || customer.name_en || customer.name || "—"
      : customer.name_en || customer.name || customer.name_ar || "—";
  };

  const normalizeListResponse = payload => {
    if (!payload) return { items: [], pagination: null };
    let items = Array.isArray(payload?.data?.data)
      ? payload.data.data
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    let pagination = payload.data?.meta || payload.meta || null;
    if (pagination) {
      pagination = {
        current_page: pagination.current_page || 1,
        last_page: pagination.last_page || 1,
        per_page: pagination.per_page || 10,
        total: pagination.total || 0,
      };
    }
    return { items, pagination };
  };

  const fetchProjects = React.useCallback(
    async (page, search) => {
      try {
        setIsProjectsLoading(true);
        const queryPage = page || projectPage || 1;
        const querySearch = search !== undefined ? search : projectSearchTerm;

        if (!isOnline()) {
          const allCachedItems = await getAllDropdownCacheForQuery(
            "report_projects",
            { per_page: 10, search: querySearch || "" },
          );
          if (allCachedItems?.length > 0) {
            dispatch({
              type: "projectReports/getProjectsForReport/fulfilled",
              payload: { data: allCachedItems.slice(0, 10) },
            });
            return;
          }
        }

        if (isOnline()) {
          const token = getToken();
          const response = await axios.get(`${BASE_URL}/projects`, {
            params: {
              page: queryPage,
              per_page: 10,
              search: querySearch || undefined,
            },
            headers: {
              Authorization: `Bearer ${token}`,
              "Accept-Language": i18n.language,
            },
          });
          const { items, pagination: paginationMeta } = normalizeListResponse(
            response.data,
          );
          dispatch({
            type: "projectReports/getProjectsForReport/fulfilled",
            payload: { data: items },
          });
          setProjectsPagination(paginationMeta);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsProjectsLoading(false);
      }
    },
    [dispatch, projectPage, projectSearchTerm],
  );

  useEffect(() => {
    setTitle("projectReports.projectSummary", { defaultValue: "ملخص المشروع" });
    fetchProjects(1);
    return () => {
      dispatch(clearProjectSummary());
    };
  }, [dispatch, fetchProjects, setTitle]);

  useEffect(() => {
    if (projectId) dispatch(clearProjectSummary());
  }, [projectId, dispatch]);

  useEffect(() => {
    if (projectDropdownOpen) {
      setPageMinHeight("150vh");
    } else {
      setPageMinHeight("auto");
    }
  }, [projectDropdownOpen]);

  useEffect(() => {
    if (success) dispatch(clearSuccess());
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  useEffect(() => {
    const handleMouseMove = e => {
      if (!isDragging) return;
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };
    const handleMouseUp = () => setIsDragging(false);
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (!projectDropdownOpen) {
      lastProjectSearchRef.current = "";
      lastProjectPageRef.current = 1;
      return;
    }

    const searchChanged = lastProjectSearchRef.current !== projectSearchTerm;
    const pageChanged = lastProjectPageRef.current !== projectPage;

    if (!searchChanged && !pageChanged && projects.length > 0) return;

    const delay = searchChanged ? 400 : 0;
    const timeoutId = setTimeout(() => {
      fetchProjects(projectPage, projectSearchTerm);
      lastProjectSearchRef.current = projectSearchTerm;
      lastProjectPageRef.current = projectPage;
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [
    projectDropdownOpen,
    projectPage,
    projectSearchTerm,
    fetchProjects,
    projects.length,
  ]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        projectDropdownOpen &&
        !e.target.closest(".project-dropdown-container")
      )
        setProjectDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [projectDropdownOpen]);

  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    if (!projectSearchTerm) return projects;
    const term = projectSearchTerm.toLowerCase();
    return projects.filter(p => {
      const nameAr = p.name_ar?.toLowerCase() || "";
      const nameEn = p.name_en?.toLowerCase() || "";
      const name = p.name?.toLowerCase() || "";
      const code = p.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) ||
        nameEn.includes(term) ||
        name.includes(term) ||
        code.includes(term)
      );
    });
  }, [projects, projectSearchTerm]);

  const projectsHasNext = useMemo(() => {
    if (!projectsPagination) return false;
    return projectsPagination.current_page < projectsPagination.last_page;
  }, [projectsPagination]);

  const handleSubmit = e => {
    e.preventDefault();
    if (!projectId) {
      toast.error(
        t("projectReports.selectProject", { defaultValue: "اختر المشروع" }),
        { rtl: isRTL },
      );
      return;
    }
    dispatch(clearProjectSummary());
    const params = { projectId };
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    dispatch(getProjectSummary(params));
  };

  const handlePreviewAll = () => {
    if (!projectSummary) {
      toast.error(t("projectReports.noData"), { rtl: isRTL });
      return;
    }
    setIsPreviewOpen(true);
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  const handlePrintAll = async () => {
    try {
      setIsGeneratingPdf(true);
      const element = document.getElementById("ultimate-ledger-card-root");
      if (!element) return;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      pdf.addImage(
        canvas.toDataURL("image/png", 1.0),
        "PNG",
        0,
        0,
        pdfWidth,
        (canvas.height * pdfWidth) / canvas.width,
        undefined,
        "FAST",
      );
      pdf.save(`Statement_Of_Account_Project_${projectId}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const formatDate = dateString => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatNumber = (value, fractionDigits = 2) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString("en-US", {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }),
    );

  const selectedProject = projects.find(
    p => String(p.id) === String(projectId),
  );

  return (
    <div
      style={{
        padding: "20px",
        direction: dir,
        minHeight: pageMinHeight,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "24px",
          border: "1px solid #333",
          flex: 1,
        }}
      >
        <h2
          style={{
            color: "white",
            marginBottom: "20px",
            textAlign: headerAlign,
          }}
        >
          {isRTL
            ? "كشف الحساب الختامي والتحليلي للمشروع"
            : "Project Account Statement & Ledger"}
        </h2>

        {/* نموذج الفلاتر واختيار المشروع */}
        <form
          onSubmit={handleSubmit}
          style={{
            backgroundColor: "#1a1f2e",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div
              style={{ position: "relative" }}
              className="project-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.project")}
              </label>
              <div
                onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  {selectedProject
                    ? getProjectName(selectedProject)
                    : t("projectReports.selectProject")}
                </div>
                <span>▼</span>
              </div>
              {projectDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  <div style={{ padding: "8px" }}>
                    <input
                      ref={projectSearchInputRef}
                      type="text"
                      placeholder="بحث..."
                      value={projectSearchTerm}
                      onChange={e => setProjectSearchTerm(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        backgroundColor: "#0d1117",
                        color: "white",
                        border: "1px solid #333",
                        borderRadius: "6px",
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                  {filteredProjects.map(p => (
                    <div
                      key={p.id}
                      onClick={() => {
                        setProjectId(String(p.id));
                        setProjectDropdownOpen(false);
                      }}
                      style={{
                        padding: "10px",
                        cursor: "pointer",
                        borderBottom: "1px solid #333",
                        color: "white",
                      }}
                    >
                      {p.code ? `${p.code} - ` : ""}
                      {getProjectName(p)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                {t("projectReports.fromDate")}
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                {t("projectReports.toDate")}
              </label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: "#AC2000",
                color: "white",
                border: "none",
                padding: "12px 30px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              {isLoading
                ? t("projectReports.loading")
                : t("projectReports.viewReport")}
            </button>
          </div>
        </form>

        {/* أزرار المعاينة والطباعة */}
        {projectSummary && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "12px",
                marginBottom: "25px",
              }}
            >
              <button
                onClick={handlePreviewAll}
                style={{
                  backgroundColor: "#374151",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: 8,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <FaEye />{" "}
                {isRTL ? "معاينة كشف الحساب الختامي" : "Preview Ledger"}
              </button>
              <button
                onClick={handlePrintAll}
                disabled={isGeneratingPdf}
                style={{
                  backgroundColor: "#06b6d4",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: 8,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <FaPrint />{" "}
                {isRTL
                  ? "تصدير كشف حساب رسمي (PDF)"
                  : "Print Official Statement"}
              </button>
            </div>
          </>
        )}

        {/* 📋 كرت كشف الحساب المالي التحليلي */}
        {projectSummary && (
          <div
            id="ultimate-ledger-card-root"
            style={{
              backgroundColor: "#1a1f2e",
              padding: "28px",
              borderRadius: "12px",
              border: "1px solid #374151",
              marginTop: "20px",
            }}
          >
            {/* 1. الترويسة العليا */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "2px solid #374151",
                paddingBottom: "16px",
                marginBottom: "24px",
                flexWrap: "wrap",
                gap: "15px",
              }}
            >
              <div>
                <h3
                  style={{
                    color: "#f3f4f6",
                    margin: "0 0 8px 0",
                    fontSize: "20px",
                  }}
                >
                  📄{" "}
                  {isRTL
                    ? "كشف حساب تفصيلي وحركة العمليات"
                    : "Statement of Account Ledger"}
                </h3>
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    marginTop: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ color: "#9ca3af" }}>
                    {isRTL ? "المشروع" : "Project"}:{" "}
                    <strong style={{ color: "#fff" }}>
                      {getCurrentProjectName()}
                    </strong>
                  </span>
                  <span style={{ color: "#9ca3af" }}>
                    {isRTL ? "العميل" : "Customer"}:{" "}
                    <strong style={{ color: "#fff" }}>
                      {getCustomerName()}
                    </strong>
                  </span>
                </div>
              </div>
              <div
                style={{
                  textAlign: isRTL ? "left" : "right",
                  color: "#9ca3af",
                  fontSize: "14px",
                }}
              >
                <div>
                  {isRTL ? "الفترة المفلترة" : "Statement Period"}:{" "}
                  {formatDate(fromDate)} - {formatDate(toDate)}
                </div>
                <div style={{ marginTop: "4px" }}>
                  <strong>
                    {isRTL ? "تاريخ استخراج التقرير" : "Run Date"}:
                  </strong>{" "}
                  {new Date().toLocaleDateString("en-US")}
                </div>
              </div>
            </div>

            {/* 2. المؤشرات المالية والتحليلات المتقدمة */}
            <h4
              style={{
                color: "#fff",
                marginBottom: "14px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FaChartBar style={{ color: "#AC2000" }} />{" "}
              {isRTL
                ? "المؤشرات والتحليلات المالية للمشروع"
                : "Financial KPIs & Performance Analytics"}
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "16px",
                  borderRadius: "8px",
                  borderLeft: "4px solid #AC2000",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "13px",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  {isRTL
                    ? "القيمة التعاقدية للمشروع"
                    : "Baseline Contract Value"}
                </span>
                <strong
                  style={{
                    color: "#fff",
                    fontSize: "18px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.contractValue)}
                </strong>
              </div>

              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "16px",
                  borderRadius: "8px",
                  borderLeft: ledgerData.isOverContract
                    ? "4px solid #a855f7"
                    : "4px solid #64748b",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "13px",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  {ledgerData.isOverContract
                    ? isRTL
                      ? "أعمال إضافية متجاوزة (متبقي للعميل)"
                      : "Contract Overage / Variations"
                    : isRTL
                      ? "المتبقي المتاح من العقد للعميل"
                      : "Remaining Project Contract"}
                </span>
                <strong
                  style={{
                    color: ledgerData.isOverContract ? "#c084fc" : "#fff",
                    fontSize: "18px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.remainingContractOrOverage)}
                </strong>
                <span
                  style={{
                    fontSize: "11px",
                    color: "#9ca3af",
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  {ledgerData.isOverContract
                    ? isRTL
                      ? "⚠️ الأعمال أعلى من العقد"
                      : "ℹ️ ضمن السقف التعاقدي"
                    : isRTL
                      ? "✓ متبقي غير منفذ"
                      : "Unbilled Contract Balance"}
                </span>
              </div>

              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "16px",
                  borderRadius: "8px",
                  borderLeft: "4px solid #3b82f6",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "13px",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  {isRTL ? "إجمالي الأعمال المنفذة" : "Total Work Done"}
                </span>
                <strong
                  style={{
                    color: "#3b82f6",
                    fontSize: "18px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.totalWorkDone)}
                </strong>
                <span
                  style={{
                    fontSize: "11px",
                    color: "#9ca3af",
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  {isRTL ? "نسبة التنفيذ من العقد" : "Progress %"}:{" "}
                  {formatNumber(ledgerData.financialProgressPct, 1)}%
                </span>
              </div>
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "16px",
                  borderRadius: "8px",
                  borderLeft: "4px solid #22c55e",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "13px",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  {isRTL
                    ? "إجمالي التحصيلات (الكاش الفعلي)"
                    : "Total Collected Revenue"}
                </span>
                <strong
                  style={{
                    color: "#22c55e",
                    fontSize: "18px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.totalCollected)}
                </strong>
                <span
                  style={{
                    fontSize: "11px",
                    color: "#9ca3af",
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  {isRTL ? "نسبة التحصيل من المنفذ" : "Collection %"}:{" "}
                  {formatNumber(ledgerData.collectionPct, 1)}%
                </span>
              </div>
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "16px",
                  borderRadius: "8px",
                  borderLeft: "4px solid #f59e0b",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "13px",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  {isRTL ? "صافي المتبقي للتحصيل" : "Net Outstanding"}
                </span>
                <strong
                  style={{
                    color: "#f59e0b",
                    fontSize: "18px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.netOutstanding)}
                </strong>
                <span
                  style={{
                    fontSize: "11px",
                    color: "#9ca3af",
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  {isRTL ? "الرصيد المتبقي المستحق" : "Balance Due"}
                </span>
              </div>
            </div>

            {/* 2.2 ملخص التسوية المحاسبية للمستخلصات */}
            <h5
              style={{
                color: "#fff",
                marginTop: "20px",
                marginBottom: "10px",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FaFileInvoiceDollar style={{ color: "#10b981" }} />{" "}
              {isRTL
                ? "تفاصيل تسوية أعمال المستخلصات"
                : "Progress Bills Deduction Summary"}
            </h5>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "12px",
                marginBottom: "25px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "12px 16px",
                  borderRadius: "6px",
                  borderTop: "3px solid #6b7280",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "12px",
                    display: "block",
                  }}
                >
                  {isRTL
                    ? "إجمالي قيمة المستخلصات قبل الخصم"
                    : "Gross Billed Amount (Before Deductions)"}
                </span>
                <strong
                  style={{
                    color: "#fff",
                    fontSize: "15px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.totalWorkDone)}
                </strong>
              </div>
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "12px 16px",
                  borderRadius: "6px",
                  borderTop: "3px solid #ef4444",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "12px",
                    display: "block",
                  }}
                >
                  {isRTL
                    ? "إجمالي المحتجز (تأمين أعمال)"
                    : "Total Retention Held"}
                </span>
                <strong
                  style={{
                    color: "#ef4444",
                    fontSize: "15px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.totalRetention)}
                </strong>
              </div>
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "12px 16px",
                  borderRadius: "6px",
                  borderTop: "3px solid #f59e0b",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "12px",
                    display: "block",
                  }}
                >
                  {isRTL
                    ? "إجمالي مسترد الدفعة المقدمة"
                    : "Advance Payment Deduction"}
                </span>
                <strong
                  style={{
                    color: "#f59e0b",
                    fontSize: "15px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.totalAdvanceDeduction)}
                </strong>
              </div>
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "12px 16px",
                  borderRadius: "6px",
                  borderTop: "3px solid #3b82f6",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "12px",
                    display: "block",
                  }}
                >
                  {isRTL ? "إجمالي ضريبة القيمة المضافة" : "Total VAT Added"}
                </span>
                <strong
                  style={{
                    color: "#3b82f6",
                    fontSize: "15px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.totalTax)}
                </strong>
              </div>
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "12px 16px",
                  borderRadius: "6px",
                  borderTop: "3px solid #a855f7",
                }}
              >
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: "12px",
                    display: "block",
                  }}
                >
                  {isRTL ? "إجمالي ضريبة الخصم" : "Total Deduction Tax"}
                </span>
                <strong
                  style={{
                    color: "#a855f7",
                    fontSize: "15px",
                    fontFamily: "monospace",
                  }}
                >
                  {formatNumber(ledgerData.totalDeductionTax)}
                </strong>
              </div>
            </div>

            {/* 3. جدول دفتر الأستاذ وحركات كشف الحساب التراكمي */}
            <h4
              style={{
                color: "#fff",
                marginBottom: "14px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FaCalculator style={{ color: "#3b82f6" }} />{" "}
              {isRTL
                ? "دفتر الأستاذ التفصيلي وحركات كشف الحساب التراكمي"
                : "Chronological Statement Ledger"}
            </h4>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  backgroundColor: "#1b3350",
                  minWidth: "700px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#111827",
                      borderBottom: "2px solid #374151",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px",
                        fontSize: "13px",
                        color: "#9ca3af",
                        border: "1px solid #374151",
                        textAlign: "center",
                      }}
                    >
                      {isRTL ? "التاريخ" : "Date"}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        fontSize: "13px",
                        color: "#9ca3af",
                        border: "1px solid #374151",
                        textAlign: "center",
                      }}
                    >
                      {isRTL ? "المرجع/الكود" : "Reference"}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        fontSize: "13px",
                        color: "#9ca3af",
                        border: "1px solid #374151",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {isRTL
                        ? "شرح وبيان الحركة المالية"
                        : "Transaction Description"}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        fontSize: "13px",
                        color: "#ef4444",
                        border: "1px solid #374151",
                        textAlign: "center",
                      }}
                    >
                      {isRTL ? "مدين (+) مستحق" : "Debit (+Billed)"}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        fontSize: "13px",
                        color: "#22c55e",
                        border: "1px solid #374151",
                        textAlign: "center",
                      }}
                    >
                      {isRTL ? "دائن (-) محصل" : "Credit (-Paid)"}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        fontSize: "13px",
                        color: "#f59e0b",
                        border: "1px solid #374151",
                        textAlign: "center",
                      }}
                    >
                      {isRTL ? "الرصيد التراكمي" : "Balance"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.StatementOfAccount.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        style={{
                          padding: "24px",
                          textAlign: "center",
                          color: "#6b7280",
                        }}
                      >
                        {isRTL
                          ? "لا توجد حركات مالية (مستخلصات أو سندات قبض) مسجلة في هذه الفترة"
                          : "No records found."}
                      </td>
                    </tr>
                  ) : (
                    ledgerData.StatementOfAccount.map((tx, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: "1px solid #374151",
                          backgroundColor:
                            idx % 2 === 0 ? "#1b3350" : "#1a1f2e",
                        }}
                      >
                        <td
                          style={{
                            padding: "12px",
                            border: "1px solid #374151",
                            textAlign: "center",
                            color: "#9ca3af",
                          }}
                        >
                          {formatDate(tx.date)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            border: "1px solid #374151",
                            textAlign: "center",
                            color: "#fff",
                            fontWeight: "bold",
                          }}
                        >
                          {tx.code}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            border: "1px solid #374151",
                            textAlign: isRTL ? "right" : "left",
                            color: tx.type === "bill" ? "#9ca3af" : "#e5e7eb",
                          }}
                        >
                          <span
                            style={{
                              marginRight: "6px",
                              color: tx.type === "bill" ? "#3b82f6" : "#22c55e",
                            }}
                          >
                            ●
                          </span>{" "}
                          {tx.description}
                          {tx.notes && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#9ca3af",
                                marginTop: "4px",
                                fontStyle: "italic",
                                marginInlineStart: "16px",
                              }}
                            >
                              {tx.notes}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            border: "1px solid #374151",
                            textAlign: "center",
                            color: tx.debit > 0 ? "#ef4444" : "#6b7280",
                            fontFamily: "monospace",
                            fontWeight: tx.debit > 0 ? "bold" : "normal",
                          }}
                        >
                          {tx.debit > 0 ? formatNumber(tx.debit) : "0.00"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            border: "1px solid #374151",
                            textAlign: "center",
                            color: tx.credit > 0 ? "#22c55e" : "#6b7280",
                            fontFamily: "monospace",
                            fontWeight: tx.credit > 0 ? "bold" : "normal",
                          }}
                        >
                          {tx.credit > 0 ? formatNumber(tx.credit) : "0.00"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            border: "1px solid #374151",
                            textAlign: "center",
                            color: "#f59e0b",
                            fontWeight: "bold",
                            fontFamily: "monospace",
                            backgroundColor: "#ffffff03",
                          }}
                        >
                          {formatNumber(tx.runningBalance)}
                        </td>
                      </tr>
                    ))
                  )}
                  {ledgerData.StatementOfAccount.length > 0 && (
                    <tr
                      style={{
                        backgroundColor: "#111827",
                        fontWeight: "bold",
                        borderTop: "2px solid #374151",
                      }}
                    >
                      <td
                        colSpan="3"
                        style={{
                          padding: "14px",
                          border: "1px solid #374151",
                          textAlign: isRTL ? "right" : "left",
                          color: "#fff",
                        }}
                      >
                        {isRTL
                          ? "إجمالي الحركات المعتمدة والرصيد الختامي المستحق"
                          : "Total Statement Summary"}
                      </td>
                      <td
                        style={{
                          padding: "14px",
                          border: "1px solid #374151",
                          textAlign: "center",
                          color: "#ef4444",
                          fontFamily: "monospace",
                          fontSize: "15px",
                        }}
                      >
                        {formatNumber(ledgerData.totalBilled)}
                      </td>
                      <td
                        style={{
                          padding: "14px",
                          border: "1px solid #374151",
                          textAlign: "center",
                          color: "#22c55e",
                          fontFamily: "monospace",
                          fontSize: "15px",
                        }}
                      >
                        {formatNumber(ledgerData.totalCollected)}
                      </td>
                      <td
                        style={{
                          padding: "14px",
                          border: "1px solid #374151",
                          textAlign: "center",
                          color: "#f59e0b",
                          fontSize: "16px",
                          fontFamily: "monospace",
                          backgroundColor: "#78350f20",
                        }}
                      >
                        {formatNumber(ledgerData.netOutstanding)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* مودال المعاينة (Preview Modal) */}
      {isPreviewOpen &&
        projectSummary &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              zIndex: 999999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "20px",
              overflow: "auto",
            }}
            onClick={e => {
              if (e.target === e.currentTarget) setIsPreviewOpen(false);
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                display: "flex",
                gap: "10px",
                zIndex: 1000000,
              }}
            >
              <button
                onClick={() => setPreviewZoom(prev => Math.min(2, prev + 0.1))}
                style={{
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                +
              </button>
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  borderRadius: "6px",
                  color: "white",
                }}
              >
                {Math.round(previewZoom * 100)}%
              </div>
              <button
                onClick={() =>
                  setPreviewZoom(prev => Math.max(0.1, prev - 0.1))
                }
                style={{
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                −
              </button>
              <button
                onClick={handlePrintAll}
                style={{
                  backgroundColor: "#06b6d4",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                {isRTL ? "تحميل كملف PDF" : "Download PDF"}
              </button>
              <button
                onClick={() => setIsPreviewOpen(false)}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div
              style={{
                transform: `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`,
                transformOrigin: "center top",
                width: "820px",
                backgroundColor: "#ffffff",
                color: "#000",
                padding: "40px",
                boxSizing: "border-box",
                marginTop: "60px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    border: "2px solid #000",
                    padding: "18px",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    direction: dir,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: "95%",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      opacity: 0.05,
                    }}
                  />
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                    }}
                  />

                  <h2
                    style={{
                      fontSize: "18px",
                      color: "#000080",
                      marginTop: "12px",
                      borderBottom: "2px solid #000",
                      paddingBottom: "6px",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {isRTL
                      ? "كشف الحساب الرسمي للمشروع"
                      : "Official Statement of Account"}
                  </h2>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: "10px 0",
                      fontSize: "13px",
                      borderBottom: "1px solid #eee",
                      paddingBottom: "8px",
                    }}
                  >
                    <div>
                      <strong>{isRTL ? "اسم المشروع" : "Project Name"}:</strong>{" "}
                      {getCurrentProjectName()}
                    </div>
                    <div>
                      <strong>{isRTL ? "اسم العميل" : "Customer Name"}:</strong>{" "}
                      {getCustomerName()}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
                      gap: "8px",
                      margin: "15px 0",
                      fontSize: "11px",
                      backgroundColor: "#f9fafb",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                      textAlign: "center",
                    }}
                  >
                    {/* Row 1: Main Financial KPIs */}
                    <div>
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "القيمة التعاقدية" : "Contract Value"}
                      </strong>
                      <span style={{ fontWeight: "bold", fontSize: "13px" }}>
                        {formatNumber(ledgerData.contractValue)}
                      </span>
                    </div>
                    <div>
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "إجمالي الأعمال المنفذة" : "Total Work Done"}
                      </strong>
                      <span
                        style={{
                          fontWeight: "bold",
                          color: "#3b82f6",
                          fontSize: "13px",
                        }}
                      >
                        {formatNumber(ledgerData.totalWorkDone)}
                      </span>
                    </div>
                    <div>
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "المتبقي من العقد" : "Remaining Contract"}
                      </strong>
                      <span style={{ fontWeight: "bold", fontSize: "13px" }}>
                        {formatNumber(ledgerData.remainingContractOrOverage)}
                      </span>
                    </div>
                    <div>
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "إجمالي التحصيلات" : "Total Collected"}
                      </strong>
                      <span
                        style={{
                          fontWeight: "bold",
                          color: "#22c55e",
                          fontSize: "13px",
                        }}
                      >
                        {formatNumber(ledgerData.totalCollected)}
                      </span>
                    </div>
                    <div>
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "صافي المتبقي للتحصيل" : "Net Outstanding"}
                      </strong>
                      <span
                        style={{
                          fontWeight: "bold",
                          color: "#f59e0b",
                          fontSize: "13px",
                        }}
                      >
                        {formatNumber(ledgerData.netOutstanding)}
                      </span>
                    </div>

                    {/* Row 2: Deductions and Taxes */}
                    <div
                      style={{
                        borderTop: "1px dashed #ccc",
                        paddingTop: "8px",
                      }}
                    >
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "تأمين الأعمال" : "Retention Held"}
                      </strong>
                      <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                        {formatNumber(ledgerData.totalRetention)}
                      </span>
                    </div>
                    <div
                      style={{
                        borderTop: "1px dashed #ccc",
                        paddingTop: "8px",
                      }}
                    >
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "مسترد الدفعة المقدمة" : "Advance Deduct"}
                      </strong>
                      <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                        {formatNumber(ledgerData.totalAdvanceDeduction)}
                      </span>
                    </div>
                    <div
                      style={{
                        borderTop: "1px dashed #ccc",
                        paddingTop: "8px",
                      }}
                    >
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "القيمة المضافة" : "VAT Added"}
                      </strong>
                      <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                        {formatNumber(ledgerData.totalTax)}
                      </span>
                    </div>
                    <div
                      style={{
                        borderTop: "1px dashed #ccc",
                        paddingTop: "8px",
                      }}
                    >
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "ضريبة الخصم" : "Deduction Tax"}
                      </strong>
                      <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                        {formatNumber(ledgerData.totalDeductionTax)}
                      </span>
                    </div>
                    <div
                      style={{
                        borderTop: "1px dashed #ccc",
                        paddingTop: "8px",
                      }}
                    >
                      <strong
                        style={{
                          display: "block",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {isRTL ? "إجمالي المستخلصات" : "Total Billed"}
                      </strong>
                      <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                        {formatNumber(ledgerData.totalBilled)}
                      </span>
                    </div>
                  </div>

                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      border: "1px solid #000",
                      marginTop: "15px",
                      fontSize: "12px",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#f3f4f6" }}>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: "center",
                            width: "36px",
                          }}
                        >
                          #
                        </th>
                        <th
                          style={{ border: "1px solid #000", padding: "6px" }}
                        >
                          {isRTL ? "التاريخ" : "Date"}
                        </th>
                        <th
                          style={{ border: "1px solid #000", padding: "6px" }}
                        >
                          {isRTL ? "المرجع/الكود" : "Reference"}
                        </th>
                        <th
                          style={{ border: "1px solid #000", padding: "6px" }}
                        >
                          {isRTL
                            ? "البيان وعمليات الحركة المستندية"
                            : "Description"}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            color: "red",
                          }}
                        >
                          {isRTL ? "مدين (+)" : "Debit"}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            color: "green",
                          }}
                        >
                          {isRTL ? "دائن (-)" : "Credit"}
                        </th>
                        <th
                          style={{ border: "1px solid #000", padding: "6px" }}
                        >
                          {isRTL ? "الرصيد المتبقي" : "Balance"}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerData.StatementOfAccount.map((tx, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              width: "36px",
                            }}
                          >
                            {idx + 1}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {formatDate(tx.date)}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}
                          >
                            {tx.code}
                          </td>
                          <td
                            style={{ border: "1px solid #000", padding: "6px" }}
                          >
                            {tx.description}
                            {tx.notes && (
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#666",
                                  marginTop: "2px",
                                  fontStyle: "italic",
                                }}
                              >
                                {tx.notes}
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {tx.debit > 0 ? formatNumber(tx.debit) : "0.00"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                            }}
                          >
                            {tx.credit > 0 ? formatNumber(tx.credit) : "0.00"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #000",
                              padding: "6px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}
                          >
                            {formatNumber(tx.runningBalance)}
                          </td>
                        </tr>
                      ))}
                      <tr
                        style={{
                          backgroundColor: "#e5e7eb",
                          fontWeight: "bold",
                        }}
                      >
                        <td
                          colSpan="4"
                          style={{ border: "1px solid #000", padding: "6px" }}
                        >
                          {isRTL
                            ? "الرصيد الإجمالي الختامي المستحق للدفع"
                            : "Total Closing Balance Due"}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: "center",
                          }}
                        >
                          {formatNumber(ledgerData.totalBilled)}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: "center",
                          }}
                        >
                          {formatNumber(ledgerData.totalCollected)}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "6px",
                            textAlign: "center",
                            color: "#b45309",
                          }}
                        >
                          {formatNumber(ledgerData.netOutstanding)}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{ width: "100%", height: "auto", marginTop: "40px", borderTop: "4px double #000", paddingTop: "8px" }}
                  />
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ProjectSummary;
